/**
 * Route Prefetching Utilities
 *
 * Intelligently prefetch likely next pages to improve navigation speed
 */

const prefetchedRoutes = new Set<string>();
const prefetchQueue: string[] = [];
let isPrefetching = false;

/**
 * Prefetch a route by creating a link element with rel="prefetch"
 */
export const prefetchRoute = (path: string): void => {
  // Skip if already prefetched
  if (prefetchedRoutes.has(path)) return;

  // Check if connection is good enough for prefetching
  const connection = (navigator as { connection?: { effectiveType?: string; saveData?: boolean } })
    .connection;
  if (connection && typeof connection === 'object') {
    const { effectiveType, saveData } = connection;
    // Don't prefetch on slow connections or when save-data is enabled
    if (saveData || effectiveType === 'slow-2g' || effectiveType === '2g') {
      return;
    }
  }

  // Add to queue
  prefetchQueue.push(path);
  processPrefetchQueue();
};

/**
 * Process the prefetch queue with rate limiting
 */
const processPrefetchQueue = () => {
  if (isPrefetching || prefetchQueue.length === 0) return;

  isPrefetching = true;
  const path = prefetchQueue.shift();

  if (!path) {
    isPrefetching = false;
    return;
  }

  // Use requestIdleCallback if available for better performance
  const prefetch = () => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = path;
    link.as = 'document';

    link.onload = link.onerror = () => {
      prefetchedRoutes.add(path);
      isPrefetching = false;

      // Process next item after a delay
      setTimeout(processPrefetchQueue, 100);
    };

    document.head.appendChild(link);
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(prefetch, { timeout: 2000 });
  } else {
    setTimeout(prefetch, 100);
  }
};

/**
 * Prefetch routes on hover with debouncing
 */
let hoverTimeout: ReturnType<typeof setTimeout> | undefined;
export const prefetchOnHover = (path: string): void => {
  clearTimeout(hoverTimeout);
  hoverTimeout = setTimeout(() => {
    prefetchRoute(path);
  }, 150); // Small delay to avoid prefetching every hover
};

/**
 * Prefetch likely next routes based on current page
 */
export const prefetchLikelyRoutes = (currentPath: string, language: string = 'en'): void => {
  const routes: Record<string, string[]> = {
    '/': [`/${language}/providers`, `/${language}/treatments`, `/${language}/doctors`],
    '/providers': [`/${language}/treatments`, `/${language}/doctors`],
    '/treatments': [`/${language}/providers`, `/${language}/doctors`],
    '/doctors': [`/${language}/providers`, `/${language}/treatments`],
    '/blog': [`/${language}/treatments`],
  };

  // Find matching routes using explicit checks for security
  let routeList: string[] | undefined;

  if (currentPath.includes('/')) {
    routeList = routes['/'];
  } else if (currentPath.includes('/providers')) {
    routeList = routes['/providers'];
  } else if (currentPath.includes('/treatments')) {
    routeList = routes['/treatments'];
  } else if (currentPath.includes('/doctors')) {
    routeList = routes['/doctors'];
  } else if (currentPath.includes('/blog')) {
    routeList = routes['/blog'];
  }

  if (routeList && routeList.length > 0) {
    // Prefetch after a delay to not interfere with page load
    setTimeout(() => {
      for (const route of routeList) {
        prefetchRoute(route);
      }
    }, 3000); // Wait 3 seconds after page load
  }
};

/**
 * Prefetch critical API data
 */
export const prefetchApiData = async (url: string): Promise<void> => {
  if (!url) return;

  try {
    // Check if browser supports Cache API
    if ('caches' in window) {
      const cache = await caches.open('api-prefetch-v1');
      const response = await cache.match(url);

      if (!response) {
        // Prefetch and cache
        const fetchResponse = await fetch(url);
        if (fetchResponse.ok) {
          await cache.put(url, fetchResponse.clone());
        }
      }
    }
  } catch {
    // Prefetch failed
  }
};

/**
 * Clear prefetch cache
 */
export const clearPrefetchCache = async (): Promise<void> => {
  if ('caches' in window) {
    await caches.delete('api-prefetch-v1');
  }
  prefetchedRoutes.clear();
};

/**
 * Hook to automatically prefetch on link hover
 */
export const usePrefetchOnHover = (): ((path: string) => void) => {
  if (typeof window === 'undefined') return () => {};

  return (path: string) => prefetchOnHover(path);
};
